import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Import all the smaller hooks
import useUploadFormState from './hooks/useUploadFormState';
import useUploadUIState from './hooks/useUploadUIState';
import useUploadDataState from './hooks/useUploadDataState';
import useUploadHandlers from './hooks/useUploadHandlers';
import useUploadNotifications from './hooks/useUploadNotifications';
import useUploadValidation from './hooks/useUploadValidation';
import useUploadVideoProcessing from './hooks/useUploadVideoProcessing';
import { usePreRegisteredUsers } from '../../contexts/userDataContext';

const useUploadVideoState = (user, typeOfUser) => {
  const queryClient = useQueryClient();

  const formState = useUploadFormState(user, typeOfUser);
  const uiState = useUploadUIState();
  const dataState = useUploadDataState(
    user,
    typeOfUser,
    formState.selectedStudent,
    formState.selectedPatient
  );

  // Get preRegisteredUsers for goal sharing updates
  const { data: preRegisteredUsers = [] } = usePreRegisteredUsers();

  const { addVideoNotification } = useUploadNotifications();
  const { validateVideoUpload } = useUploadValidation();
  const { processVideoUpload } = useUploadVideoProcessing();

  const handlers = useUploadHandlers(
    formState,
    uiState,
    uiState.setError,
    formState.setFileDuration,
    formState.setGoalAssociatedWithBehavior,
    formState.setSelectCategory,
    formState.setSelectedBehaviorIds,
    formState.setSelectedSkillIds,
    formState.setSelectedGoalIds,
    formState.setOtherInputValue,
    formState.setNewStudent,
    formState.setNewPatient,
    uiState.setShowDialog,
    formState.setSelectedStudent,
    formState.setSelectedPatient,
    uiState.setIsResetConfirmed,
    formState.setTherapyType,
    formState.setInterventions,
    formState.setAntecedent,
    formState.setConsequence,
    formState.setEditAndViewIds,
    formState.setViewOnlyIds,
    formState.setVideoFeedBackNotesPrivate,
    formState.setInformation,
    formState.setOpenVideoUpload,
    typeOfUser
  );

  // Effects
  useEffect(() => {
    uiState.setIsFirstSectionComplete(
      Boolean(
        formState.customFileName &&
          formState.date &&
          (formState.newPatient || formState.newStudent) &&
          formState.uploadedFile
      )
    );
  }, [
    formState.customFileName,
    formState.selectedPatient,
    formState.selectedStudent,
    formState.date,
    formState.uploadedFile,
    formState.newPatient,
    formState.newStudent,
    uiState,
  ]);

  useEffect(() => {
    if (uiState.isResetConfirmed) {
      formState.setGoalAssociatedWithBehavior(false);
      formState.setInformation({ notes: '' });
      uiState.setIsResetConfirmed(false);
    }
  }, [uiState.isResetConfirmed, formState, uiState]);

  useEffect(() => {
    if (!formState.openVideoUpload) {
      uiState.setError({ show: false, message: '' });
      formState.setUploadedFile(null);
      formState.setInformation({ notes: '' });
      formState.setDate(null);
      formState.setSelectedPatient('');
    }
  }, [formState.openVideoUpload, formState, uiState]);

  // Main video save handler
  const videoSaveHandler = async () => {
    const isValid = validateVideoUpload(
      formState.uploadedFile,
      formState.date,
      formState.selectedPatient,
      formState.selectedStudent,
      formState.therapyType,
      formState.antecedent,
      formState.consequence,
      formState.interventions,
      formState.goalAssociatedWithBehavior,
      formState.selectedGoalIds,
      formState.selectCategory,
      formState.selectedBehaviorIds,
      formState.selectedSkillIds,
      formState.otherInputValue,
      formState.information,
      typeOfUser,
      uiState.setError
    );

    if (!isValid) return;

    await processVideoUpload(
      formState.uploadedFile,
      formState.customFileName,
      formState.fileDuration,
      formState.date,
      user,
      typeOfUser,
      formState.selectedStudent,
      formState.selectedPatient,
      formState.therapyType,
      formState.interventions,
      formState.antecedent,
      formState.consequence,
      formState.selectCategory,
      formState.goalAssociatedWithBehavior,
      formState.selectedGoalIds,
      formState.selectedBehaviorIds,
      formState.selectedSkillIds,
      formState.otherInputValue,
      formState.editAndViewIds,
      formState.viewOnlyIds,
      formState.information,
      formState.videoFeedBackNotesPrivate,
      uiState.setIsLoading,
      uiState.setError,
      addVideoNotification,
      dataState.patientData,
      dataState.students,
      dataState.teacherData || [],
      queryClient,
      handlers.handleClose,
      preRegisteredUsers
    );
  };


  return {
    ...formState,
    ...uiState,
    ...dataState,
    ...handlers,
    videoSaveHandler,
    queryClient,
  };
};

export default useUploadVideoState;
